import { LocationOption } from "@/offering/fm_location_picker";
import { Brand } from "@/offering_v2/brand";
import { ImageData, ImageVersion } from "./image";

interface BaseLocation {
  id: string;
  name: string;
  streetAddress: string;
  city: string;
  timezone: string;
}

interface LocationInfo extends BaseLocation {
  wlHomeLink: string | null;
  country: string | null;
  state: string;
  stateName: string;
  /** The district inside the city (e.g. Mayfair in London). Optional, used only
   * to group locations on booking pages; never part of the postal address. */
  neighborhood?: string;
  postalCode: string | null;
  direction: string | null;
  longitude: number | null;
  latitude: number | null;
}

export interface Location extends LocationInfo {
  /** The brand this location trades under, when it is not the partner's own. */
  brand?: Brand;
  logo: ImageData | null;
  maxCapacity: number;
  currencyCode: string | null;
  pricingInclusion: string | null;
  images: ImageVersion[];
  googleAction?: GoogleAction;
  locationDisplay?: string;
  /** Partner-set custom label for this location, resolved from its offering
   * items. Undefined when unset. Lets catalog surfaces show the same name the
   * offering/product pages already do, instead of the generated "city - street". */
  customLocationDisplay?: string;
}

interface GoogleAction {
  entityId: string;
  name: string;
  active: boolean;
}

/** The address levels a location picker can group by. */
export type LocationGroupKey = "city" | "stateName" | "neighborhood";

/** The group a location falls under. A location missing the finer level
 * (state or neighborhood) falls back to its city, so grouping never drops it. */
export function getLocationGroupValue(
  location: Pick<LocationInfo, "city" | "stateName" | "neighborhood">,
  key: LocationGroupKey
): string {
  if (key === "city") return location.city ?? "";
  return location[key] || location.city || "";
}

export interface PartnerLocation extends LocationInfo {
  contactPhone: string;
  phoneNumberMask: string;
}

export const OPTION_ALL_LOCATION: LocationOption = {
  city: "",
  streetAddress: "",
  id: "",
  name: "All locations",
};
